import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Button, Typography, Switch, Tooltip, Empty, Spin } from "antd";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";
import FormSection from "./FormSection";
import { sortSections } from "./helper/formHelpers";
import { StyledFormContainer } from "./styles";
import CreateCase from "./CreateCase";
import { useDispatch, useSelector } from "react-redux";
import { addEditCase } from "../../../../../store/chats/thunks";
import CasesModal from "./CasesModal";
import { selectSelectedCaseForm } from "../../../../../store/chats/selectors";
import httpClient from "../../../../../api/httpClient";
import { convertDatesToDayjs } from "../../../../../utils/dayjs";
import { setKeyValue } from "../../../../../store/chats/slice";
import PDFGenerator from "./PDFGenerator";

const Cases = ({ conversation_id, tenant_id }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCasesModal, setShowCasesModal] = useState(false);
  const [showHints, setShowHints] = useState(true);
  const [fetchingForms, setFetchingForms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formsData, setFormsData] = useState(null);
  const [form] = Form.useForm();

  const selectedCaseForm = useSelector(selectSelectedCaseForm);

  const dispatch = useDispatch();

  const sortedSections = useMemo(
    () => sortSections(formsData?.sections || []),
    [formsData],
  );

  useEffect(() => {
    if (!selectedCaseForm?.id) return;

    if (selectedCaseForm.id === "create_case") {
      setShowAddModal(true);
      dispatch(
        setKeyValue({
          key: "selectedCaseForm",
          value: null,
        }),
      );
      return;
    }

    (async () => {
      try {
        setFetchingForms(true);
        const { data } = await httpClient.get("cases", {
          params: {
            tenant_id,
            case_template_id: selectedCaseForm?.case_type_id,
          },
        });

        setFormsData({ ...data, isEdit: true });

        form.setFieldsValue({
          ...convertDatesToDayjs(selectedCaseForm?.case_data_json || {}),
          id: selectedCaseForm?.id,
        });

        dispatch(
          setKeyValue({
            key: "selectedCaseForm",
            value: null,
          }),
        );
      } finally {
        setFetchingForms(false);
      }
    })();
  }, [selectedCaseForm]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);
        await dispatch(
          addEditCase({
            case_data_json: values,
            conversation_id,
            tenant_id,
            case_type_id: formsData?.id,
            case_type: formsData?.name,
            id: formsData?.isEdit ? values?.id : undefined,
          }),
        );
        setFormsData(null);
      } catch (error) {
        console.log("🚀 ~ index.jsx:17 ~ Cases ~ error:", error);
      } finally {
        setSubmitting(false);
      }
    },
    [sortedSections, formsData, conversation_id, tenant_id],
  );

  return (
    <StyledFormContainer>
      <div className="productivity-padding">
        <div className="flex justify-space-between align-center">
          <Typography.Title level={4} className="cases-title">Cases</Typography.Title>
          <div className="flex align-center justify-center header-action">
            <Tooltip title={showHints ? "Hide hints" : "Show hints"}>
              <Switch
                checked={showHints}
                onChange={setShowHints}
                checkedChildren="✨"
                unCheckedChildren="🚫"
              />
            </Tooltip>
            <Button size="small" onClick={() => setShowCasesModal(true)}>
              All Cases
            </Button>
            <div
              onClick={(e) => {
                e.preventDefault();
                setShowAddModal(true);
              }}
              className="cursor-pointer"
            >
              <PlusOutlined />
            </div>
          </div>
        </div>
        {fetchingForms ? (
          <div
            className="flex justify-center h-100 w-100"
            style={{ marginTop: "10px" }}
          >
            <Spin />
          </div>
        ) : formsData ? (
          <Form
            form={form}
            layout={"horizontal"}
            onFinish={handleSubmit}
            size="small"
            disabled={submitting}
          >
            {sortedSections.map((section, i) => (
              <FormSection
                section={section}
                disabled={submitting}
                key={i}
                showHints={showHints}
              />
            ))}

            <Form.Item name="id" hidden />

            <div
              className="flex align-center justify-end"
              style={{ padding: "20px 0px" }}
            >
              <PDFGenerator form={form} template={formsData} />

              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                disabled={submitting}
                loading={submitting}
              >
                Save
              </Button>
            </div>
          </Form>
        ) : (
          <Empty className="forms-not-found" description="Forms Not Selected" />
        )}
      </div>
      {showAddModal && (
        <CreateCase
          onCancel={() => setShowAddModal(false)}
          onOpen={(d) => {
            form.resetFields();
            const values = {};
            (d?.sections || [])?.forEach((s) => {
              if (s.is_repeating) {
                values[s.id] = [{}];
              }
            });
            form.setFieldsValue(values);
            setFormsData(d);
          }}
        />
      )}
      {showCasesModal && (
        <CasesModal onCancel={() => setShowCasesModal(false)} />
      )}
    </StyledFormContainer>
  );
};

export default Cases;
